import { useEffect, useRef } from "react";

interface WakeLockSentinelLike {
  release: () => Promise<void>;
  addEventListener?: (type: "release", listener: () => void) => void;
}
interface WakeLockNavigator {
  wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> };
}

/**
 * Holds a screen wake-lock while `enabled`. The lock is automatically released by the
 * browser when the tab hides, so we re-acquire on `visibilitychange → visible`. No-op
 * (and `supported:false`) when navigator.wakeLock is absent (e.g. iOS Safari quirks —
 * spec §6.4 iOS reality). Best-effort: request rejections (e.g. not focused) are swallowed.
 */
export function useWakeLock(enabled: boolean): { supported: boolean } {
  const nav =
    typeof navigator !== "undefined"
      ? (navigator as unknown as WakeLockNavigator)
      : undefined;
  const supported = !!nav?.wakeLock;
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);

  useEffect(() => {
    if (!supported || !nav?.wakeLock) return;
    let cancelled = false;

    const acquire = async (): Promise<void> => {
      if (!enabled || document.visibilityState !== "visible" || sentinelRef.current) return;
      try {
        const s = await nav.wakeLock!.request("screen");
        if (cancelled) {
          void s.release();
          return;
        }
        sentinelRef.current = s;
        // The browser auto-releases the lock when the tab hides. Listen for that so
        // sentinelRef clears and the visibilitychange→visible handler can re-acquire
        // (otherwise the stale sentinel would block re-acquisition forever).
        s.addEventListener?.("release", () => {
          if (sentinelRef.current === s) sentinelRef.current = null;
        });
      } catch {
        /* best-effort: browser may reject when not focused */
      }
    };
    const release = (): void => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) void s.release().catch(() => { /* ignore */ });
    };
    const onVisibility = (): void => {
      if (enabled && document.visibilityState === "visible") void acquire();
    };

    if (enabled) void acquire();
    else release();

    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      release();
    };
  }, [enabled, supported, nav]);

  return { supported };
}
